import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  LinearProgress,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import FileField from '../../component/shared/component/FileFieldV2'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { usePostGenerateCP58 } from '../../api/cp-58'

const SUPPORTED_FORMATS = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}

const FILE_SIZE = 4 * 1000 * 1000 //4MB

// Yup runs all tests, hence need to check whether file exists before performing further validation
const validationSchema = yup.object({
  file: yup
    .mixed()
    .test('required', 'File is required', (file) => {
      return file instanceof File
    })
    .test('xlsx', 'Only .xlsx files are allowed', (file) => {
      if (!file) return false

      return file.name.toLowerCase().endsWith('.xlsx')
    })
    .test('fileSize', 'File must be smaller than 4 MB', (file) => {
      if (!file) return false

      return file.size <= FILE_SIZE
    }),
})

const Import = ({ setOpen, open, refetch }) => {
  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
    register,
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = usePostGenerateCP58()

  const [errorRes, setErrorRes] = useState(null)

  const onClose = () => {
    // Reset form
    reset()

    // Remove errors
    setErrorRes(null)

    // Close dialog
    setOpen(false)
  }

  const onSubmit = async (values) => {
    // Init form data
    const formData = new FormData()

    // Append file
    formData.append('file', values.file)

    try {
      // Post
      const response = await mutateAsync(formData)

      // Refresh data
      await refetch()

      const { data = {} } = response || {}

      const { insertedCount = 0, skippedCount = 0, duplicateEntries = [] } = data

      const duplicateLines = duplicateEntries.map(
        (d) => `• ${d.salesId} — ${d.htgCompany} (${d.monthYear})`
      )

      const successMessage = [
        `Successfully imported ${insertedCount} record${insertedCount !== 1 ? 's' : ''}.`,
        skippedCount > 0
          ? `Skipped ${skippedCount} duplicate entr${
              skippedCount === 1 ? 'y' : 'ies'
            }:\n${duplicateLines.join('\n')}`
          : null,
      ]
        .filter(Boolean)
        .join('\n')

      enqueueSnackbar(successMessage, {
        variant: 'success',
        persist: true,
        style: { whiteSpace: 'pre-line' },
        action: (key) => (
          <IconButton onClick={() => closeSnackbar(key)} color='inherit' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        ),
      })

      onClose()
    } catch (e) {
      const errorData = e?.response?.data?.errors || []
      const message =
        errorData.length > 0
          ? errorData.map((err) => `• ${err.msg}`).join('\n')
          : 'Something went wrong. Please try again.'

      setErrorRes(message)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      {/* Loader */}
      {isLoading && <LinearProgress color='success'></LinearProgress>}
      <DialogTitle>Import CP 58</DialogTitle>
      {/* Import Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='h6'>
                Please submit a .xlsx import file to generate CP 58 forms
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='body2'>
                Example template used for import,{' '}
                <a
                  href={'/documents/CP58_Import_Template.xlsx'}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  click to download
                </a>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FileField
                inputProps={{
                  ...register('file'),
                  onChange: (e) => {
                    setValue('file', e?.target?.files[0])

                    // Manually trigger field/yup validation if form submitted once
                    // This is to reset failed validation after submit
                    if (isSubmitted) {
                      trigger('file')

                      // Remove errors
                      setErrorRes(null)
                    }
                  },
                  type: 'file',
                  accept: SUPPORTED_FORMATS,
                }}
                error={errors?.file}
                helperText={errors?.file && errors?.file?.message}
                label={'File Import'}
                required={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        {/* Import Error Listing */}
        {errorRes && (
          <DialogContent>
            <Typography variant='h6' color='error'>
              Import Errors
            </Typography>
            <Typography variant='body2' color='error'>
              Failed to import! Please refer to errors below for more information
            </Typography>
            {/* pre-line to preserve new lines / \n */}
            <Typography color='error' mt={2} style={{ whiteSpace: 'pre-line' }}>
              {errorRes}
            </Typography>
          </DialogContent>
        )}
        {/* Actions */}
        <DialogActions>
          <Button variant='outlined' onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            Import
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default Import
